import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table, Form } from "react-bootstrap";
import ExportToExcel from "../components/ExportToExcel";
import Pagination from "../components/Pagination";
import SearchBar from "../components/SearchBar";
import { useNavigate } from "react-router-dom";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Card from "react-bootstrap/Card";
import FilterForm from "./FilterForm";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import Select from "react-select";
import { confirm } from "../components/Confirmation";
import { useSelector } from "react-redux";
import { RotateCcw } from "lucide-react";

export const REJECT_STATUS_LACK_OF_DOCS = 7038;
export const REJECT_STATUS_BLACKLIST = 7039;
// Candidate Status IDs Constants
const CANDIDATE_R1_TO_BE_SCHEDULED = 6972;
const CANDIDATE_R1_SCHEDULED = 6965;
const CANDIDATE_R2_TO_BE_SCHEDULED = 6973;
const CANDIDATE_R2_SCHEDULED = 6966;
const CANDIDATE_R3_TO_BE_SCHEDULED = 6974;
const CANDIDATE_R3_SCHEDULED = 6967;
const CANDIDATE_CLIENT_INTERVIEW = 6975;
const CANDIDATE_HR_TO_BE_SCHEDULED = 6976;
const CANDIDATE_HR_SCHEDULED = 6968;
const CANDIDATE_TO_BE_OFFERED = 6977;
const CANDIDATE_OFFER_APPROVAL_PENDING = 6978;
const CANDIDATE_OFFERED = 1320;
const CANDIDATE_REJECT_LACK_OF_DOCS = 6969;
const CANDIDATE_R1_REJECT = 7015;
const CANDIDATE_R2_REJECT = 7016;
const CANDIDATE_R3_REJECT = 7017;
const CANDIDATE_HR_REJECT = 7018;
const CANDIDATE_COULD_NOT_OFFER = 7019;
const CANDIDATE_OFFER_ACCEPT = 1322;
const CANDIDATE_OFFER_DECLINE = 1323;

const SCHEDULABLE_STATUSES = [
  CANDIDATE_R1_TO_BE_SCHEDULED,
  CANDIDATE_R1_SCHEDULED,
  CANDIDATE_R2_TO_BE_SCHEDULED,
  CANDIDATE_R2_SCHEDULED,
  CANDIDATE_R3_TO_BE_SCHEDULED,
  CANDIDATE_R3_SCHEDULED,
  CANDIDATE_CLIENT_INTERVIEW,
  CANDIDATE_HR_TO_BE_SCHEDULED,
  CANDIDATE_HR_SCHEDULED
];

const isSchedulableStatus = (status) =>
  SCHEDULABLE_STATUSES.some(
    (s) => String(s) === String(status) || Number(s) === Number(status)
  );

const MultiAction = ({
  data,
  columns,
  defaultColumns,
  // itemsPerPage,
  handleReset,
  searchTerm,
  setSearchTerm,
  handleIcon1,
  isApiStatus,
  handleIcon2,
  handleIcon3,
  handleIcon4,
  handleIcon5,
  showIcon1,
  showIcon2,
  showIcon3,
  showIcon4,
  showIcon5,
  icons,
  createEmp,
  formData,
  handleChange,
  handleDropDownChange,
  handleSubmit,
  jobtitleopts,
  statusopts,
  hropts,
  sourceopts,
  skillsopts,
  revokeOffer,
}) => {
  const dropdownRef = useRef(null);

  const closeDropdown = () => {
    if (dropdownRef.current) {
      dropdownRef.current.classList.remove("show");
    }
  };

  const handleFormSubmit = async () => {
    await handleSubmit();
    closeDropdown();
  };

  const [visibleColumns, setVisibleColumns] = useState(
    Object.keys(columns).reduce((acc, key) => {
      acc[key] = columns[key].visible; // Set initial visibility based on the 'visible' key
      return acc;
    }, {})
  );

  const ExcelName = useSelector(
    ({ layoutReducer }) => layoutReducer?.filterSlice?.navbarHeading
  );
  // const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // const toggleColumn = (columnName) => {
  //   setVisibleColumns({
  //     ...visibleColumns,
  //     [columnName]: !visibleColumns[columnName],
  //   });
  // };
  const toggleColumn = (columnName) => {
    setVisibleColumns((prevState) => ({
      ...prevState,
      [columnName]: !prevState[columnName], // Toggle visibility
    }));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const navigate = useNavigate();

  const handleEdit = (url, id) => {
    navigate(`${url}${id}`);
  };
  const handleSchedule = (url, candidate_id, job_description_id) => {
    navigate(`${url}${candidate_id}/${job_description_id}`);
  };

  const sortedData = useMemo(() => {
    let sortableItems = [...data] || [];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key] ? String(a[sortConfig.key]) : "";
        const valB = b[sortConfig.key] ? String(b[sortConfig.key]) : "";

        // Split the values into alphabetical and numeric parts
        const partsA = valA.match(/(\D+|\d+)/g) || [];
        const partsB = valB.match(/(\D+|\d+)/g) || [];

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
          const partA = partsA[i] || "";
          const partB = partsB[i] || "";

          // Determine if parts are numeric or alphabetical
          const isNumericA = !isNaN(partA);
          const isNumericB = !isNaN(partB);

          if (isNumericA && isNumericB) {
            // Both parts are numeric, so compare as numbers
            const numA = parseInt(partA, 10);
            const numB = parseInt(partB, 10);
            if (numA !== numB) {
              return sortConfig.direction === "ascending"
                ? numA - numB
                : numB - numA;
            }
          } else if (isNumericA || isNumericB) {
            // One part is numeric and the other is alphabetical
            return isNumericA ? -1 : 1;
          } else {
            // Both parts are alphabetical, so compare as strings
            if (partA !== partB) {
              return sortConfig.direction === "ascending"
                ? partA.localeCompare(partB)
                : partB.localeCompare(partA);
            }
          }
        }

        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
  const recordsPerPageOption = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: filteredData.length, label: "All" },
  ];

  const handleSort = (columnName) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === columnName &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key: columnName, direction });
  };
  // handleMouseOver
  const handleMouseOver = () => {

    // Perform any action you want when the mouse hovers over the element
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredData.length);
  const currentData = filteredData.slice(startIndex, endIndex);

  const createEmpfn = async (candidate_id) => {
    let result = await createEmp(candidate_id);
    return result;
  };

  const revokeOfferfn = async (candidate_id) => {
    let result = await revokeOffer(candidate_id);
    return result;
  };

  const handleRecordsPerPage = (e) => {
    const newRecordsPerPage = Number(e.target.value);

    // Calculate the new starting record index based on the current page
    const currentStartIndex = (currentPage - 1) * selectedRecordsPerPage;
    const newPage = Math.floor(currentStartIndex / newRecordsPerPage) + 1;

    // Update state
    setSelectedRecordsPerPage(newRecordsPerPage);
    setCurrentPage(newPage); // Adjust to the new page based on the same starting record index
    setItemsPerPage(newRecordsPerPage);
  };

  return (
    <div>
      {/* <FilterForm
              formData={formData}
              handleChange={handleChange}
              handleDropDownChange={handleDropDownChange}
              handleSubmit={handleSubmit}
              jobtitleopts={jobtitleopts}
              statusopts={statusopts}
              hropts={hropts}
              sourceopts={sourceopts}
              skillsopts={skillsopts}
            /> */}
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          <div className="d-flex">
            <div className="me-2">
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </div>
          <div className="dropdown">
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="tooltip-bottom">Filters</Tooltip>}
            >
              <button
                className="dropdown-toggle"
                type="button"
                id="excel-btn"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i class="bi bi-funnel-fill"></i>
              </button>
            </OverlayTrigger>

            <div
              className="dropdown-menu custom-dropdown-width"
              aria-labelledby="excel-btn"
              ref={dropdownRef}
              onClick={(e) => e.stopPropagation()}
            >
              <FilterForm
                formData={formData}
                handleChange={handleChange}
                handleDropDownChange={handleDropDownChange}
                handleSubmit={handleFormSubmit}
                jobtitleopts={jobtitleopts}
                statusopts={statusopts}
                handleReset={handleReset}
                hropts={hropts}
                sourceopts={sourceopts}
                skillsopts={skillsopts}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <div className="dropdown me-2">
            {/* <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="tooltip-bottom">Upload Candidate CV</Tooltip>
              }
            >
              <button
                data-bs-toggle="dropdown"
                id="excel-btn">
                <i className="bi bi-upload"></i>
              </button>
            </OverlayTrigger> */}

            <div
              className="dropdown-menu custom-dropdown-width"
              aria-labelledby="excel-btn"
              onClick={(e) => e.stopPropagation()}
            >
              {/* <FilterForm
                formData={formData}
                handleChange={handleChange}
                handleDropDownChange={handleDropDownChange}
                handleSubmit={handleSubmit}
                jobtitleopts={jobtitleopts}
                statusopts={statusopts}
                hropts={hropts}
                sourceopts={sourceopts}
                skillsopts={skillsopts}
              /> */}
              <div className="text-center">Upload CV (Pending)</div>
            </div>
          </div>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="tooltip-bottom">Export To excel</Tooltip>}
          >
            <div className="me-1">
              <ExportToExcel
                visiblecolumns={visibleColumns}
                data={filteredData}
                filename={`${ExcelName}.csv`}
              />
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="tooltip-bottom">Column Chooser</Tooltip>}
          >
            <div className="btn-group">
              <button
                type="button"
                className="dropdown-toggle skill-button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                id="excel-btn"
              >
                <i className="bi bi-list"></i>
              </button>

              <ul className="dropdown-menu p-2">
                <div className="row">
                  <Form.Group>
                    {Object.keys(columns).map((columnName) => (
                      <Form.Check
                        key={columnName}
                        type="checkbox"
                        id={`checkbox-${columnName}`}
                        label={columns[columnName].text} //custom column name
                        checked={visibleColumns[columnName] || false}
                        onChange={() => toggleColumn(columnName)}
                      />
                    ))}
                  </Form.Group>
                </div>
              </ul>
            </div>
          </OverlayTrigger>
        </div>
      </div>
      <div className="box-body table-responsive">
    <Table striped bordered hover>
  <thead id="thcolor" className="text-center align-middle">
    <tr>
      {Object.keys(columns).map(
        (columnName) =>
          visibleColumns[columnName] && (
            <th 
              key={columnName} 
              onClick={() => handleSort(columnName)}
              style={{ cursor: "pointer", verticalAlign: "middle" }}
              className="align-middle"
            >
              <div className="d-inline-flex align-items-center justify-content-center">
                <span>{columns[columnName].text}</span>
                <span className="ms-2 d-inline-flex align-items-center">
                  {sortConfig.key === columnName ? (
                    sortConfig.direction === "ascending" ? (
                      <i className="bi bi-chevron-up"></i>
                    ) : (
                      <i className="bi bi-chevron-down"></i>
                    )
                  ) : (
                    <i className="bi bi-chevron-down" style={{ opacity: 0.5 }}></i>
                  )}
                </span>
              </div>
            </th>
          )
      )}
      <th className="align-middle" style={{ verticalAlign: "middle" }}>Action</th>
    </tr>
  </thead>

  <tbody>
    {isApiStatus === undefined ? (
      // Loading state
      <tr>
        <td colSpan={Object.keys(columns).filter(col => visibleColumns[col]).length + 1} className="text-center">
          <FaSpinner className="spinner" />
        </td>
      </tr>
    ) : currentData.length === 0 ? (
      // No data state
      <tr>
        <td colSpan={Object.keys(columns).filter(col => visibleColumns[col]).length + 1} className="text-center">
          No Data Found
        </td>
      </tr>
    ) : (
      // Data rows
      currentData.map((item, index) => {
        const isInactive =
          (item.isJobInactive === 1 || item.isJobInactive === "1") &&
          Boolean(item.RequisitionID);
        return (
          <tr key={item.ID || index}>
          {Object.keys(columns).map((columnName) => {
            const isDateColumn =
              columns[columnName].text.toLowerCase().includes("doj") ||
              columns[columnName].text.toLowerCase().includes("joining");

            const isEllipsisColumn =
              columnName === "SkillSet" ||
              columnName === "FullName" ||
              columnName === "JobTitle" ||
              columnName === "CandidateReferralType" ||
              columnName === "RecruiterName" ||
              columnName === "ReferralName" ||
              columnName === "CandidateStatus";

             return (
              visibleColumns[columnName] && (
                <td
                  style={{
                    maxWidth: "130px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  className={`${isDateColumn ? "text-center" : "text-start"} ${
                    isEllipsisColumn ? "ellipsis-column" : ""
                  }`}
                  key={columnName}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    {isEllipsisColumn ? (
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip id={`tooltip-${columnName}`}>
                            {item[columnName]}
                          </Tooltip>
                        }
                      >
                        <span
                          className="ellipsis-text"
                          style={{
                            display: "inline-block",
                            maxWidth: "100px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item[columnName]}
                        </span>
                      </OverlayTrigger>
                    ) : (
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: "100px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item[columnName]}
                      </span>
                    )}
                    {columnName === "CandidateStatus" && (
                      <RevokeOfferButton
                        candidateID={item.ID}
                        fullName={item.FullName}
                        status={item.CandidateStatus_181}
                        revokeOffer={revokeOfferfn}
                      />
                    )}
                  </div>
                </td>
              )
            );
          })}

          <td style={isInactive ? { opacity: 0.5, pointerEvents: "none" } : {}}>
            <div className="d-flex justify-content-center text-center">
              {showIcon1 && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="tooltip-bottom">Edit</Tooltip>}
                >
                  <div
                    className="me-2"
                    onMouseOver={handleMouseOver}
                    onClick={() =>
                      handleEdit(
                        "/candidate-in-process/candidate/edit/",
                        item.ID
                      )
                    }
                  >
                    {icons.Icon1}
                  </div>
                </OverlayTrigger>
              )}
              {showIcon2 && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    isSchedulableStatus(item.CandidateStatus_181) ? (
                      <Tooltip id="tooltip-bottom">
                        Schedule Interview
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <div
                    disabled={
                      !isSchedulableStatus(item.CandidateStatus_181)
                    }
                    className={
                      isSchedulableStatus(item.CandidateStatus_181)
                        ? "me-2"
                        : "me-2 icon-disabled"
                    }
                    onClick={() =>
                      isSchedulableStatus(item.CandidateStatus_181)
                        ? handleSchedule(
                            "/candidate-in-process/schedule-interview/",
                            item.ID,
                            item.JobDescriptionID
                          )
                        : {}
                    }
                  >
                    {icons.Icon2}
                  </div>
                </OverlayTrigger>
              )}
              {showIcon3 && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    (item.CandidateStatus_181 == CANDIDATE_TO_BE_OFFERED || item.CandidateStatus_181 == CANDIDATE_OFFER_APPROVAL_PENDING) ? (
                      <Tooltip id="tooltip-bottom">Offer Letter</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <div
                    disabled={
                      (item.CandidateStatus_181 == CANDIDATE_TO_BE_OFFERED || item.CandidateStatus_181 == CANDIDATE_OFFER_APPROVAL_PENDING) ? false : true
                    }
                    className={
                      (item.CandidateStatus_181 == CANDIDATE_TO_BE_OFFERED || item.CandidateStatus_181 == CANDIDATE_OFFER_APPROVAL_PENDING)
                        ? "me-2"
                        : "me-2 icon-disabled"
                    }
                    onClick={() =>
                      (item.CandidateStatus_181 == CANDIDATE_TO_BE_OFFERED || item.CandidateStatus_181 == CANDIDATE_OFFER_APPROVAL_PENDING)
                        ? handleSchedule(
                            "/candidate-in-process/offer-letter/",
                            item.ID,
                            item.JobDescriptionID
                          )
                        : {}
                    }
                  >
                    {icons.Icon3}
                  </div>
                </OverlayTrigger>
              )}
              {showIcon4 && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="tooltip-bottom">HR Action</Tooltip>}
                >
                  <div
                    className="me-2"
                    onClick={() =>
                      handleSchedule(
                        "/candidate-in-process/hr-authority/",
                        item.ID,
                        item.JobDescriptionID
                      )
                    }
                  >
                    {icons.Icon4}
                  </div>
                </OverlayTrigger>
              )}

              <EmpIconButton
                candidate_id={item.ID}
                fullName={item.FullName}
                status={item.CandidateStatus_181}
                candidateStatus={item.CandidateStatus}
                createEmpfn={createEmpfn}
                icon={icons.Icon5}
              />
            </div>
          </td>
        </tr>
        );
      })
    )}
  </tbody>
</Table>

        <div className="d-flex justify-content-between mb-3">
          <div>
            <span className="d-flex">
              Showing {(currentPage - 1) * selectedRecordsPerPage + 1} -{" "}
              {Math.min(
                currentPage * selectedRecordsPerPage,
                filteredData.length
              )}{" "}
              of {filteredData.length} <span className="ms-1">results</span>
              <select
                value={selectedRecordsPerPage}
                onChange={handleRecordsPerPage}
                className="mx-2 form-select-sm"
              >
                {recordsPerPageOption.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <div>
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiAction;

const EmpIconButton = (props) => {
  const createEmpChild = async (candidate_id) => {

    let confirm_create = await confirm(`Are you sure you want to onboard ${props.fullName} as a NICE employee?`);
    if (confirm_create) {
      setCreateEmp(true);
      let result = await props.createEmpfn(candidate_id);
      if (result) {
        toast.success(`Employee created successfully.`);
      } else {
        toast.error(`Something went wrong.`);
      }
      setCreateEmp(false);
    }
  };
  const [createEmpFlag, setCreateEmp] = useState(false);
  return (
    <>
      {createEmpFlag && props.candidateStatus == "Verified" ? (
        <>
          <FaSpinner className="sm-spinner" />
        </>
      ) : (
        <OverlayTrigger
          placement="bottom"
          overlay={
            props.candidateStatus == "Verified" ? (
              <Tooltip id="tooltip-bottom">Create Employee</Tooltip>
            ) : (
              <></>
            )
          }
        >
          <div
            disabled={props.candidateStatus == "Verified" ? false : true}
            className={
              props.candidateStatus == "Verified"
                ? "me-2"
                : "me-2 icon-disabled"
            }
            onClick={
              props.candidateStatus == "Verified"
                ? () => createEmpChild(props.candidate_id)
                : () => { }
            }
          >
            {props.icon}
          </div>
        </OverlayTrigger>
      )}
    </>
  );
};

const RevokeOfferButton = ({ candidateID, fullName, status, revokeOffer }) => {
  const handleRevoke = async () => {
    let confirmRevoke = await confirm(`Are you sure you want to revoke the offer letter for ${fullName}?`);
    if (confirmRevoke) {
      let result = await revokeOffer(candidateID);
      if (result) {
        toast.success(`Offer letter revoked successfully.`);
      } else {
        toast.error(`Something went wrong.`);
      }
    }
  };

  // Statuses: 1320 (Offered), 1322 (Offer Accepted), 1323 (Offer Declined)
  const showRevoke = [CANDIDATE_OFFERED, CANDIDATE_OFFER_ACCEPT, CANDIDATE_OFFER_DECLINE].includes(Number(status));

  if (!showRevoke) return null;

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={<Tooltip id="tooltip-bottom">Revoke Offer</Tooltip>}
    >
      <span
        className="ms-2 d-inline-flex align-items-center"
        style={{ cursor: "pointer", color: "#dc3545" }}
        onClick={handleRevoke}
      >
        <RotateCcw size={16} />
      </span>
    </OverlayTrigger>
  );
};
